import { EditorState, Modifier, RichUtils, SelectionState, convertFromRaw, convertToRaw } from 'draft-js';
import _ from 'lodash';

export function insertLink(editorState, url) {
  const selection = getSelection(editorState);
  const contentStateWithEntity = editorState.getCurrentContent().createEntity('LINK', 'MUTABLE', { url });
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  let newSelection = selection;
  let newEditorState;

  if (!selection.isCollapsed()) {
    newEditorState = RichUtils.toggleLink(editorState, selection, entityKey);
  } else {
    const offset = selection.getStartOffset();
    const focusOffset = offset + url.length;
    const blockKey = selection.getStartKey();
    const linkRange = new SelectionState({
      anchorOffset: offset,
      anchorKey: blockKey,
      focusOffset,
      focusKey: blockKey,
    });

    const content = editorState.getCurrentContent();
    const newContent = Modifier.insertText(content, selection, url);
    newEditorState = RichUtils.toggleLink(
      EditorState.push(editorState, newContent, 'insert-characters'),
      linkRange,
      entityKey
    );

    newSelection = new SelectionState({
      anchorOffset: focusOffset,
      anchorKey: blockKey,
      focusOffset,
      focusKey: blockKey,
    });
  }

  return EditorState.forceSelection(newEditorState, newSelection);
}

export function hasLinksInSelection(editorState) {
  return !!getSelectedLinks(editorState).length;
}

export function removeLinksInSelection(editorState) {
  return getSelectedLinks(editorState).reduce(
    (prevState, { key, range }) => removeLink(prevState, key, range),
    editorState
  );
}

function getSelectedLinks(editorState) {
  return _(getSelectedBlocks(editorState)).flatMap(block => getSelectedLinksInBlock(block, editorState)).value();
}

function getSelectedBlocks(editorState) {
  const blocks = editorState.getCurrentContent().getBlocksAsArray();
  const selection = getSelection(editorState);
  const firstIndex = _.findIndex(blocks, block => block.getKey() === selection.getAnchorKey());
  const lastIndex = _.findLastIndex(blocks, block => block.getKey() === selection.getFocusKey());

  return blocks.slice(firstIndex, lastIndex + 1);
}

function getSelectedLinksInBlock(block, editorState) {
  const selectionRange = getSelectionRange(editorState, block);

  return getLinkRangesInBlock(block, editorState.getCurrentContent())
    .filter(linkRange => isInSelectionRange(selectionRange, linkRange))
    .map(linkRange => ({
      key: block.getKey(),
      range: linkRange,
    }));
}

function getSelectionRange(editorState, block) {
  const selection = getSelection(editorState);
  const blockKey = block.getKey();
  const anchorKey = selection.getAnchorKey();
  const focusKey = selection.getFocusKey();
  const anchorOffset = selection.getAnchorOffset();
  const focusOffset = selection.getFocusOffset();
  let range;

  if (anchorKey === blockKey && focusKey === blockKey) {
    range = [anchorOffset, focusOffset];
  } else if (anchorKey === blockKey) {
    range = [anchorOffset, block.getLength()];
  } else if (focusKey === blockKey) {
    range = [0, focusOffset];
  } else {
    range = [0, block.getLength()];
  }

  return range;
}

function getLinkRangesInBlock(block, contentState) {
  const ranges = [];
  block.findEntityRanges(
    value => {
      const key = value.getEntity();
      return key && contentState.getEntity(key).type === 'LINK';
    },
    (start, end) => ranges.push([start, end])
  );

  return ranges;
}

function isInSelectionRange([start, end], range) {
  return !(start <= range[0] && end <= range[0]) && !(start >= range[1] && end >= range[1]);
}

function removeLink(editorState, blockKey, [start, end]) {
  let selection = SelectionState.createEmpty(blockKey);
  selection = selection.set('anchorOffset', start);
  selection = selection.set('focusOffset', end);

  return RichUtils.toggleLink(editorState, selection, null);
}

function getSelection(editorState) {
  let selection = editorState.getSelection();

  if (selection.getIsBackward()) {
    selection = new SelectionState({
      anchorKey: selection.getFocusKey(),
      anchorOffset: selection.getFocusOffset(),
      focusKey: selection.getAnchorKey(),
      focusOffset: selection.getAnchorOffset(),
    });
  }

  return selection;
}

export const createEditorState = (contentState, decorator) =>
  contentState ?
    EditorState.createWithContent(convertFromRaw(contentState), decorator) :
    EditorState.createEmpty(decorator);

export const removeEntity = (editorState, blockKey, entityKey) => {
  const blocksToRemove = [blockKey];
  const raw = convertToRaw(editorState.getCurrentContent());

  const blockIndex = _.findIndex(raw.blocks, block => block.key === blockKey);
  const nextBlockIndex = blockIndex + 1;
  if (raw.blocks[nextBlockIndex]) {
    const nextBlock = raw.blocks[nextBlockIndex];
    if (nextBlock.type === 'unstyled' && !nextBlock.text.length) {
      blocksToRemove.push(nextBlock.key);
    }
  }

  const previousBlockIndex = blockIndex - 1;
  const blockToSelect = raw.blocks[previousBlockIndex] || raw.blocks[0];

  raw.blocks = _.filter(raw.blocks, block => blocksToRemove.indexOf(block.key) === -1);
  delete raw.entityMap[entityKey];

  const currentSelection = SelectionState.createEmpty(blockToSelect.key);
  const nextState = createEditorState(raw);

  return EditorState.forceSelection(nextState, currentSelection);
};

export const removeBlock = (editorState, blockKey) => {
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(blockKey);
  const previousBlock = contentState.getBlockBefore(blockKey);
  const selectionRange = new SelectionState({
    anchorOffset: previousBlock.text.length,
    anchorKey: previousBlock.key,
    focusOffset: block.text.length,
    focusKey: blockKey,
  });
  const newContentState = Modifier.removeRange(contentState, selectionRange, 'forward');
  return EditorState.push(editorState, newContentState, 'remove-range');
};

export const cleanUpContent = content => {
  const isBlockEmpty = block => block.type === 'unstyled' && !block.text;
  const createPredicate = indexPredicate => (block, index, blocks) =>
    !isBlockEmpty(block) || indexPredicate(index, blocks.length) || blocks.length < 2;

  const blocks = _(content.blocks)
    .filter(({ type, entityRanges }) => type !== 'atomic' || entityRanges.length)
    .filter(createPredicate(_.identity))
    .filter(createPredicate((index, blockCount) => index !== blockCount - 1))
    .value();

  return {
    ...content,
    blocks,
  };
};

export const isAtomicBlockFocused = editorState => {
  const { anchorKey, focusKey } = editorState.getSelection();
  const block = editorState.getCurrentContent().getBlockForKey(anchorKey).type;
  return anchorKey === focusKey && block === 'atomic';
};

export const getFocusableContainerId = identifier => `text-editor-container-${identifier}`;



// WEBPACK FOOTER //
// ./app/components/text-editor/modifiers/text-editor-utils.js